import { supabase } from "@/lib/supabase";

/**
 * Buckets Supabase Storage utilisés par l'application.
 *
 * À créer dans le dashboard Supabase (Storage → New bucket) :
 *   - dossier-documents (privé)
 *   - bri-files (privé)
 *   - bri-annexes (lecture publique)
 *   - message-attachments (privé)
 *   - tenant-assets (lecture publique)
 *   - library-content (lecture publique)
 */
export const STORAGE_BUCKETS = {
  dossierDocuments: "dossier-documents",
  briFiles: "bri-files",
  briAnnexes: "bri-annexes",
  messageAttachments: "message-attachments",
  tenantAssets: "tenant-assets",
  libraryContent: "library-content",
} as const;

/**
 * Upload d'un fichier dans le bucket Storage du dossier courant.
 *
 * Convention de chemin : `dossiers/{dossier_id}/{kind}/{uuid-filename}`
 */
export async function uploadDossierDocument(opts: { dossierId: string; kind: string; file: File }) {
  const { dossierId, kind, file } = opts;
  const ext = file.name.includes(".") ? file.name.split(".").pop() : "bin";
  const stamp = Date.now().toString(36);
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `dossiers/${dossierId}/${kind}/${stamp}-${safeName}`;

  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKETS.dossierDocuments)
    .upload(path, file, {
      cacheControl: "3600",
      contentType: file.type || `application/${ext}`,
      upsert: false,
    });
  if (error) throw error;
  return { path: data.path, sizeBytes: file.size, mimeType: file.type };
}

/**
 * Génère une URL signée (privée) pour télécharger un document.
 * Expiration en secondes (par défaut 1h).
 */
export async function getSignedUrl(
  bucket: keyof typeof STORAGE_BUCKETS,
  path: string,
  expiresInSeconds = 3600,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKETS[bucket])
    .createSignedUrl(path, expiresInSeconds);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Supprime un fichier (privé).
 */
export async function removeFile(bucket: keyof typeof STORAGE_BUCKETS, path: string) {
  const { error } = await supabase.storage.from(STORAGE_BUCKETS[bucket]).remove([path]);
  if (error) throw error;
}
